import React, { useEffect, useMemo } from "react";

import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchPersonCoverageHistory,
  type CoverageHistoryEvent,
} from "../../../Store/Reducers/TeamViewsSlice";

export type AssignmentHistoryTarget = {
  personUserId: string;
  personName: string;
  accountId: string;
  accountName: string;
};

type AssignmentHistoryModalProps = {
  open: boolean;
  target: AssignmentHistoryTarget | null;
  onClose: () => void;
};

function formatHistoryDate(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatHistoryEventLabel(event: CoverageHistoryEvent): string {
  const payload = event.payload || {};
  switch (event.event_type) {
    case "team_member_reassigned":
      return payload.successor_user_id
        ? "Reassigned to a successor"
        : "Removed / left unassigned";
    case "pair_transferred":
      return "Coverage transferred to successor";
    case "member_unlinked_pair_dormant":
      return "Removed — pairs left dormant";
    case "pair_left_dormant":
      return "Left dormant";
    case "hole_dismissed":
      return "Hole dismissed (history retained)";
    case "pair_transfer_rejected":
      return "Transfer cancelled";
    default:
      return String(event.event_type || "Event").replace(/_/g, " ");
  }
}

/** Prototype §7.4 — read-only assignment history for one person × account. */
const AssignmentHistoryModal: React.FC<AssignmentHistoryModalProps> = ({
  open,
  target,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const personUserId = target?.personUserId
    ? String(target.personUserId)
    : null;
  const events = useAppSelector(state =>
    personUserId
      ? state.teamViews.coverageHistoryByPerson[personUserId] || []
      : []
  );
  const loading = useAppSelector(state => state.teamViews.personHistoryLoading);
  const error = useAppSelector(state => state.teamViews.personHistoryError);

  useEffect(() => {
    if (!open || !personUserId) return;
    dispatch(fetchPersonCoverageHistory(personUserId));
  }, [dispatch, open, personUserId]);

  const accountEvents = useMemo(() => {
    if (!target?.accountId) return [];
    const accountId = String(target.accountId);
    return events.filter(e => String(e.account_id) === accountId);
  }, [events, target?.accountId]);

  const subtitle = target ? `${target.personName} — ${target.accountName}` : "";

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      size="lg"
      usePortal
      className="rl-prototype-skin"
      title="Assignment history"
      subtitle={subtitle}
      footer={
        <div className="flex justify-end">
          <Button variant="outline-primary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading history…</p>
      ) : null}
      {!loading && error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : null}
      {!loading && !error && !accountEvents.length ? (
        <p className="text-sm text-gray-500">
          No history recorded yet. History entries are created by reassign /
          remove actions on this account.
        </p>
      ) : null}
      {!loading && !error && accountEvents.length ? (
        <div className="max-h-[360px] overflow-y-auto">
          <table className="tv-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Event</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {accountEvents.map(event => (
                <tr key={event.event_id}>
                  <td>{formatHistoryDate(event.created_at)}</td>
                  <td>{formatHistoryEventLabel(event)}</td>
                  <td>{event.actor_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </CommonModal>
  );
};

export default AssignmentHistoryModal;
